import React, { useEffect, useState } from 'react';

import { GridApi } from 'ag-grid-community';

import { Paper } from '@material-ui/core';
import { Box } from '@mui/material';

import { Vector as VectorSource } from 'ol/source';
import { Geometry } from 'ol/geom';
import olMap from 'ol/Map';

import {
    useIsRoutesPaneVisible,
    useSelectedCategory,
    useSelectedDates,
    useSelectedRoutes,
} from '../../state/ui/planning/routes/index.hooks';
import {
    useIsTasksPaneVisible,
    useSelectedTaskId,
    useSelectedTasks,
} from '../../state/ui/planning/tasks/index.hooks';

import TranslationHelper from '../../helpers/TranslationHelper';

import { useStyles } from './Themable.hooks';

import EditOverlay from '../../components/EditOverlay';

import View from '../../components/View';
import PageContent from '../../components/PageContent';
import PlanningRoutesMap from './components/PlanningRoutesMap/PlanningRoutesMap';
import RoutesPane from './components/PlanningRoutes/RoutesPane/RoutesPane';
import { onFeatureSelected } from './utils';
import PlanningTaskPane from './components/PlanningTasks/PlanningTaskPane/PlanningTaskPane';
import { usePlanningDragAndDrop } from './hooks/usePlanningDragAndDrop';
import { useAppDispatch, useToolkitDispatch } from '../../hooks';
import { clearForm } from '../../state/ui/forms';
import {
    fetchPlanningTasks,
    resetTasksPaneMode,
    selectPlanningTask,
    selectTasks,
} from '../../state/ui/planning/tasks';
import { fetchRoutes, resetRouteMode } from '../../state/ui/planning/routes';
import { Panel, PanelGroup } from 'react-resizable-panels';
import CustomResizeHandle from '@/app/components/CustomResizeHandle';
import GridSection from './components/GridSection';
import useAsyncThunkWithSnackbar from '@/app/hooks/useAsyncThunkWithSnackbar';

const PlanningPage = () => {
    const dispatch = useAppDispatch();
    const toolkitDispatch = useToolkitDispatch();
    const routesPaneVisible = useIsRoutesPaneVisible();
    const tasksPaneVisible = useIsTasksPaneVisible();
    const classes = useStyles();
    const selectedTasks = useSelectedTasks();
    const taskId = useSelectedTaskId();
    const [routesGridApi, setRoutesGridApi] = useState<GridApi | undefined>(
        undefined
    );
    const [tasksGridApi, setTasksGridApi] = useState<GridApi | undefined>(
        undefined
    );
    const [source, setSource] = useState<VectorSource<Geometry> | null>(null);
    const [map, setMap] = useState<olMap | null>(null);
    const selectedRoutes = useSelectedRoutes();
    const activityCategoryId = useSelectedCategory();
    const dates = useSelectedDates();
    const handleAsyncRequest = useAsyncThunkWithSnackbar();

    const handleSetTasksGridApi = (gridApi: any) => {
        setTasksGridApi(gridApi);
    };
    const handleSetRoutesGridApi = (gridApi: any) => {
        setRoutesGridApi(gridApi);
    };
    const handleClearTask = () => {
        toolkitDispatch(resetTasksPaneMode());
        toolkitDispatch(selectPlanningTask(null));
        dispatch(clearForm({ type: 'task' }));
    };
    const handleClearRoute = () => {
        toolkitDispatch(resetRouteMode());
    };

    const refetchGridsData = () => {
        if (activityCategoryId) {
            handleAsyncRequest({
                asyncAction: fetchPlanningTasks({
                    selectedRoutes,
                    activityCategoryId,
                    dates,
                }),
            });
            handleAsyncRequest({
                asyncAction: fetchRoutes({
                    from: dates.from,
                    to: dates.to,
                    activityCategoryId,
                }),
            });
        }
    };

    const {
        dragAndDropOptionsRoutes,
        dragAndDropOptionsTasks,
        onDragStop,
        highlightedRow,
        setHighlightedRow,
        highlightedRowRef,
    } = usePlanningDragAndDrop(routesGridApi, tasksGridApi);

    useEffect(
        function refreshHighlightedRow() {
            if (highlightedRow && routesGridApi) {
                routesGridApi.redrawRows();
            }
        },
        [highlightedRow, routesGridApi]
    );

    const handleSelectFeatures = (items: number[], id: number | null) => {
        if (id && items.includes(id)) {
            toolkitDispatch(selectPlanningTask(id));
        } else if (taskId && !items.includes(taskId)) {
            toolkitDispatch(selectPlanningTask(null));
        }
        dispatch(selectTasks(items));
    };

    useEffect(() => {
        if (!tasksGridApi || !routesGridApi) {
            return;
        }
        const dropZoneParams = routesGridApi.getRowDropZoneParams({
            onDragStop,
            onDragLeave: () => {
                highlightedRowRef.current = null;
                setHighlightedRow(null);
                routesGridApi.redrawRows();
            },
        });

        tasksGridApi.removeRowDropZone(dropZoneParams);
        tasksGridApi.addRowDropZone(dropZoneParams);
    }, [tasksGridApi, routesGridApi, onDragStop]);

    useEffect(() => {
        onFeatureSelected(source, selectedTasks, map);
    }, [source, selectedTasks, map]);

    return (
        <View title={TranslationHelper.translate('Routes planning')}>
            <PageContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            width:
                                routesPaneVisible || tasksPaneVisible
                                    ? 'calc(100% - 410px)'
                                    : '100%',
                        }}
                    >
                        <PanelGroup
                            direction="horizontal"
                            autoSaveId="persistence"
                        >
                            <Panel defaultSize={60}>
                                <GridSection
                                    handleSetRoutesGridApi={
                                        handleSetRoutesGridApi
                                    }
                                    handleSetTasksGridApi={
                                        handleSetTasksGridApi
                                    }
                                    dragAndDropOptionsRoutes={
                                        dragAndDropOptionsRoutes
                                    }
                                    dragAndDropOptionsTasks={
                                        dragAndDropOptionsTasks
                                    }
                                    handleClearTask={handleClearTask}
                                    handleClearRoute={handleClearRoute}
                                    refetchGridsData={refetchGridsData}
                                />
                            </Panel>

                            <CustomResizeHandle direction="horizontal" />
                            {routesPaneVisible && (
                                <Paper className={classes.paneWrapper}>
                                    <RoutesPane />
                                </Paper>
                            )}
                            {tasksPaneVisible && (
                                <Paper className={classes.paneWrapper}>
                                    <PlanningTaskPane
                                        handleClearTask={handleClearTask}
                                        refetchGridsData={refetchGridsData}
                                    />
                                </Paper>
                            )}
                            <Panel defaultSize={40}>
                                <PlanningRoutesMap
                                    selectItems={handleSelectFeatures}
                                    selectedItems={selectedTasks}
                                    source={source}
                                    setSource={setSource}
                                    map={map}
                                    setMap={setMap}
                                />
                            </Panel>
                        </PanelGroup>
                    </Box>
                </Box>
            </PageContent>
            <EditOverlay />
        </View>
    );
};

export default PlanningPage;
